import { useRef } from 'react';
import { motion } from 'framer-motion';
import { Download, Share2, Sparkles } from 'lucide-react';
import { toPng } from 'html-to-image';
import { FacebookIcon, WhatsappIcon } from 'react-share';
import { toast } from 'react-toastify';
import { NameData } from '../types';

interface NameShareableImageProps {
  nameData: NameData;
}

export default function NameShareableImage({ nameData }: NameShareableImageProps) {
  const imageRef = useRef<HTMLDivElement>(null);

  const downloadImage = async () => {
    if (!imageRef.current) return;
    
    try {
      const dataUrl = await toPng(imageRef.current, {
        quality: 1.0,
        pixelRatio: 2
      });
      
      const link = document.createElement('a');
      link.download = `name-meaning-${nameData.name}.png`;
      link.href = dataUrl;
      link.click();
      
      toast.success('Image downloaded successfully!');
    } catch (error) {
      console.error('Failed to generate image:', error);
      toast.error('Failed to download image. Please try again.');
    }
  };

  const handleShare = async (platform: 'facebook' | 'whatsapp') => {
    const shareUrl = window.location.href;
    const title = `Meaning of the name ${nameData.name}`;
    const text = `${nameData.nepali_meaning}\n\nFind more name meanings at:`;

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(title + '\n' + text)}`,
      whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(title + '\n' + text + '\n' + shareUrl)}`
    };

    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  return (
    <div className="space-y-6">
      <div 
        ref={imageRef}
        className="relative bg-gradient-to-br from-purple-100 to-pink-100 p-8 rounded-lg"
      >
        <div className="relative z-10">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Sparkles className="text-purple-600" size={24} />
            <h3 className="text-2xl font-bold text-purple-900">
              {nameData.name}
            </h3>
            <Sparkles className="text-purple-600" size={24} />
          </div>
          
          <p className="text-lg text-gray-700 mb-4">
            {nameData.nepali_meaning}
          </p>
          
          {nameData.personality && (
            <p className="text-purple-600 italic mb-4">
              {nameData.personality}
            </p>
          )}
          
          <div className="flex items-center justify-center gap-2">
            <span className="px-4 py-2 bg-white bg-opacity-50 rounded-full text-purple-600 font-medium">
              {nameData.popularity}% popularity
            </span>
          </div>
        </div>
        
        <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none rotate-[-30deg]">
          <p className="text-4xl font-bold text-purple-900">WWW.ABH.COM.NP</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 justify-center">
        <button
          onClick={downloadImage}
          className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Download size={20} />
          Download Image
        </button>

        <button
          onClick={() => handleShare('facebook')}
          className="flex items-center gap-2 px-6 py-3 bg-[#1877f2] text-white rounded-lg hover:bg-[#166fe5] transition-colors"
        >
          <FacebookIcon size={20} round />
          Share on Facebook
        </button>

        <button
          onClick={() => handleShare('whatsapp')}
          className="flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-lg hover:bg-[#20bd5a] transition-colors"
        >
          <WhatsappIcon size={20} round />
          Share on WhatsApp
        </button>
      </div>
    </div>
  );
}